<template>
  <div div class="outline-custom w-full h-full" tabindex="0">
    <div class="relative">
      <radial-progress-bar
        v-if="questionSet.fields && questionSet.fields.length"
        :diameter="80"
        :completed-steps="countAnswers()"
        :total-steps="totalQuestions"
        class="fixed right-0 z-50"
      >
        <p class="text-l text-gray-800">
          {{ countAnswers() }} / {{ totalQuestions }}
        </p>
      </radial-progress-bar>
      <div
        v-for="(question, id) in questionSet.fields"
        :key="id"
        v-bind:id="question.linkId"
        v-bind:class="[
          question.enableWhen.length > 0
            ? 'selectHide'
            : 'hoVtvg pt-32 sm:pt-32 md:pt-32 lg:pt-12 xl:pt-12',
        ]"
      >
        <choice
          v-if="question.type === 'choice'"
          :index="id"
          :question="question"
          :error="questionError(question.linkId)"
          @answered="onQuestionAnswered"
          @action="handleChoiceAction"
        >
        </choice>
        <instruction
          v-else-if="question.type === 'instruction'"
          :instruction="question"
          @action="handleActionButton"
        >
        </instruction>
        <long-text
          v-else-if="question.type === 'long-text'"
          :error="questionError(question.linkId)"
          :index="id"
          :question="question"
          @action="handleActionButton"
          @input="onQuestionAnswered">
        </long-text>
        <accordion
          v-else-if="isMobile() && question.type === 'matrix'"
          :error="questionError(question.linkId)"
          :index="id"
          :question="question"
          @answered="onQuestionAnswered"
        ></accordion>
        <matrix
          v-else-if="question.type === 'matrix'"
          :error="questionError(question.linkId)"
          :index="id"
          :question="question"
          @answered="onQuestionAnswered"
        ></matrix>
      </div>
    </div>
  </div>
</template>

<script>
import Accordion from "@/components/Accordion.vue";
import Choice from "@/components/Choice.vue";
import Instruction from "@/components/Instruction.vue";
import LongText from "@/components/LongText.vue";
import Matrix from "@/components/Matrix.vue";
import RadialProgressBar from "@/components/ProgressBar.vue";
import surveyMixin from "../mixins/surveyMixin";
import translateMixin from "../mixins/translateMixin";
import { store } from "@/store/survey";

export default {
  mixins: [surveyMixin, translateMixin],
  data() {
    return {
      answer: {},
      errorSet: [],
      questionSet: {},
      totalQuestions: 0,
    };
  },
  components: {
    Accordion,
    Choice,
    Instruction,
    LongText,
    Matrix,
    RadialProgressBar,
    // Choice, Instruction
  },
  methods: {
    countAnswers() {
      let answerCount = 0;
      for (let answerItem in this.answer) {
        if (this.answer[answerItem] instanceof Object) {
          answerCount += Object.entries(this.answer[answerItem]).length;
        } else answerCount++;
      }
      return answerCount;
    },
    async getNextQuestionLinkId(question, orderNum) {
      // if first matrix in set
      if (question.type === 'matrix' && question.items.length === 10 && typeof(orderNum) === 'number') {
        // check if there is a sub-matrix and assign it as the nextQuestion
        const nextQuestion = await this.questionSet.fields.find(
        (x) => x.orderNum === orderNum + 'A')
        // if its a matrix that has no sub matrices, go to next question
        if (!nextQuestion) {
          const nextQuestion = await this.questionSet.fields.find((x) => x.orderNum === orderNum + 1);
          if (nextQuestion) return nextQuestion.linkId;
        } else return nextQuestion.linkId;
      // if it's a sub matrix
      } else if (question.type === 'matrix' && typeof(orderNum) === "string") {
        // find what the letter is on the orderNum
        const lastLetter = orderNum.charAt(orderNum.length-1);
        // check if there is another sub-matrix and assign it as the nextQuestion
        const nextQuestion = await this.questionSet.fields.find(
        (x) => x.orderNum === orderNum + String.fromCharCode(
              lastLetter.charCodeAt() + 1))
        // if there are no other sub matrices, go to next question
        if (!nextQuestion) {
          const nextQuestion = await this.questionSet.fields.find((x) => x.orderNum === Number(orderNum.slice(0, -1)) + 1); //remove letter from end of orderNum
          return nextQuestion.linkId;
        } else return nextQuestion.linkId;
      } else {
        const nextQuestion = await this.questionSet.fields.find((x) => x.orderNum === orderNum + 1);
        if (nextQuestion) return nextQuestion.linkId;
        else return null;
      }
    },
    goToQuestion(linkId) {
      console.log(`scrolling to question: ${linkId}`);
      this.$scrollTo(`#${linkId}`, 500, {
        offset: 100,
      });
    },
    // handleActionButton from surveyMixin
    handleChoiceAction() {},
    isMobile() {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        return true;
      }
      return false;
    },
    async onQuestionAnswered(question, answer) {
      const nextLinkId = await answer.action
        ? answer.action
        : await this.getNextQuestionLinkId(question, question.orderNum);
      if (!answer && this.answer[question.linkId])
        await this.$delete(this.answer, question.linkId);
      else {
        if (question.type === "matrix") {
          await this.$set(this.answer, question.linkId, answer);
          // check matrix subanswers equal amount of subquestions, if yes go to next question
          for (let response in await this.answer) {
            if (
              question.items.length ===
              Object.keys(this.answer[response]).length && question.linkId === response) {
              await this.goToQuestion(nextLinkId);
            }
          }
        }
        else if (question.type === "long-text") {
          await this.$set(this.answer, question.linkId, answer);
        } else {
          this.$set(this.answer, question.linkId, answer.value);
          await this.goToQuestion(nextLinkId);
        }
      }
      // record answer in store
      store.setSurveyAnswer(
        this.questionSet.identifier,
        question.linkId,
        answer
      );
    },
    questionError(questionLinkId) {
      if (this.errorSet.find((x) => x[questionLinkId])) {
        return 'You missed a question. Please try again.'
      }
    },
    async validateSet() {
      // loop over all the questions
      for (let question of this.questionSet.fields) {
        // if question required, check there's an answer
        if (question.required === true && question.type !== "instruction") {
          try {
            let answer = {}
            // if matrix deep inspect questions by passing props to compare amount of questions to answers
            if (question.type === 'matrix') {
            answer = await store.getQuestionAnswer(
              this.questionSet.identifier,
              question.linkId,
              question.type,
              question.items.length
            // else just check that there is an answer
            )} else {
              answer = await store.getQuestionAnswer(
                this.questionSet.identifier,
                question.linkId,
                question.type
            )}
            // if there is an answer returned
            if (Object.keys(answer).length !== 0) {
              // check there wasn't already an error and remove question from errorSet if there was
              if (this.questionError(question.linkId)) { 
                this.errorSet.splice(question.linkId, 1)
              }
              // then submit survey
              await store.submitSurvey();
              console.log(`Survey submitted!`);
            }
          } catch (e) {
            console.log(`survey validation error: ${question.linkId} - ${e}`);
            // if there isn't already an error then add question to the error set
            if (!this.questionError(question.linkId)) {
              await this.errorSet.push({
                [question.linkId]: e,
              });
            }
          }
        }
      }
      // if there are errors
      if (this.errorSet.length !== 0) {
        // map errorset keys as an array
        const erroredQuestions = await Object.keys(this.errorSet[0]);
        // go to first question that hasn't been answered in the array
        await this.goToQuestion(erroredQuestions[0]);
      }
      return this.errorSet.length === 0;
    },
  },
  async created() {
    this.questionSet = await this.getSection(
      this.$route.params.surveyId,
      this.$route.params.lang,
      this.$route.params.itemId
    ); // getSection() from surveyMixin
    // loop over questionSet and count the number of questions to display on the progress bar
    await this.questionSet.fields.forEach((question) => {
      if (question.type === "matrix") {
        this.totalQuestions += question.items.length // add length of sub-questions present to tally
      }
      else if (question.type !== "instruction") {
        this.totalQuestions += 1 // else +1 to tally per standard question
      }
    });
    // process matrix sub-matrices if matrix has more than ten questions
    for (let [index, question] of this.questionSet.fields.entries()) {
      // if the matrix has more than ten subquestions...
      if (question.type === "matrix" && question.items.length > 10) {
        console.log(`preparing to copy and split the matrix questions`);
        // loop over matrix questions and split into groups of ten sub-questions
        let reducedQuestions = [];
        for (let j = 0; j < question.items.length; j += 10) {
          let sliceIt = await question.items.slice(j, j + 10);
          await reducedQuestions.push(sliceIt);
        }
        // count how many sub-matrices need to be created
        const matrixCount = reducedQuestions.length;
        // record original question prefix
        const prefix = question.prefix
        // for every sub-matrix that is required
        for (let i = 0; i < matrixCount; i++) {
          // if this is not the first sub-matrix question
          if (i !== 0) {
            // create copy of the current question object
            let matrixQuestion = await Object.assign({}, question);
            // replace array of sub-questions with new sub-question tally (<= 10 questions)
            matrixQuestion.items = reducedQuestions[i];
            // update question prefix to include submatrix count
            matrixQuestion.prefix = `${prefix} (${i + 1}/${matrixCount})`;
            // splice copy of new matrixQuestion into the main questions array
            await this.questionSet.fields.splice(index + i, 0, matrixQuestion);
          // if this is the first sub-matrix
          } else {
            // push array of new sub-questions into matrixQuestion
            this.questionSet.fields[index].items = await reducedQuestions[i];
            // update question prefix to include submatrix count
            this.questionSet.fields[index].prefix = await `${prefix} (1/${matrixCount})`;
          }
          // add a letter to the end of each submatrix orderNum and linkId
          if (i !== 0) {
            this.questionSet.fields[index + i].orderNum += String.fromCharCode(
              "A".charCodeAt() + i - 1
            );
            this.questionSet.fields[index + i].linkId += String.fromCharCode(
              "A".charCodeAt() + i - 1
            );
          }
        }
      }
    }
  },
};
</script>
